import React from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: "primary" | "success" | "warning" | "danger" | "ghost" | "neutral" | "outline";
    size?: "xs" | "sm" | "md" | "lg";
    isLoading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
    className = "",
    variant = "primary",
    size = "md",
    isLoading = false,
    children,
    disabled,
    ...props
}, ref) => {
    const baseStyles = "relative flex items-center justify-center transition-all duration-75 font-bold jagged-corners disabled:opacity-50 disabled:cursor-not-allowed disabled:grayscale";

    const variants = {
        primary: "bg-[#d7ccc8] hover:bg-[#c6b8b3] border-2 border-[#5d4037] border-b-4 text-[#3e2723]",
        success: "bg-[#66bb6a] hover:bg-[#4caf50] border-2 border-[#1b5e20] border-b-4 text-white",
        warning: "bg-[#ffcc80] hover:bg-[#ffa726] border-2 border-[#e65100] border-b-4 text-[#bf360c]",
        danger: "bg-[#ef5350] hover:bg-[#e53935] border-2 border-[#b71c1c] border-b-4 text-white",
        neutral: "bg-[#f0e6d2] hover:bg-[#e6dac0] border-b-4 border-[#d7c9a9] text-wood-darker",
        ghost: "bg-transparent hover:bg-black/5 text-wood-darker border-transparent",
        outline: "bg-transparent border-2 border-wood-medium text-wood-medium hover:bg-wood-light/20",
    };

    const sizes = {
        xs: "px-2 py-0.5 text-[10px]",
        sm: "px-3 py-1 text-xs",
        md: "px-4 py-2 text-sm md:text-base",
        lg: "px-6 py-3 text-lg",
    };

    const shadowStyle = variant === 'ghost' || variant === 'outline' || disabled
        ? {}
        : { boxShadow: 'inset -4px -4px 0px 0px rgba(0,0,0,0.2), inset 4px 4px 0px 0px rgba(255,255,255,0.3)' };

    return (
        <button
            ref={ref}
            className={`
        ${baseStyles}
        ${variants[variant]}
        ${sizes[size]}
        ${className}
        active:translate-y-1 active:border-b-2 active:shadow-none
      `}
            disabled={disabled || isLoading}
            style={shadowStyle}
            {...props}
        >
            {isLoading ? (
                <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Loading...
                </span>
            ) : children}
        </button>
    );
});

Button.displayName = "Button";
